'use strict';

angular.module('ringbaPortal.services').factory('ivrNodeProcessorFactory', ivrNodeProcessorFactory);

function ivrNodeProcessorFactory(
  _,
  $http,
  ngAuthSettings
) {
  'ngInject';

  const defaultNodeProcessor = {
    attachNode: attachNode,
    updateNode: updateNode,
    updateLink: updateLink,
    removeNode: removeNode,
    getAvailableNodes: getAvailableNodes,
    getAvailableTags: getAvailableTags,
    getNodeTypeIcon: getNodeTypeIcon,
    unpackResult: unpackResult
  };

  const nodeTypeProcessors = {
    'RouterNode': _getRouterNodeProcessor(),
    'RecordNode': _getRecordNodeProcessor(),
    'MenuNode': _getMenuNodeProcessor(),
    'HangupNode': _getHangupNodeProcessor(),
    'DialNode': _getDialNodeProcessor(),
    'GatherNode': _getGatherNodeProcessor(),
    'GoToNode': _getGotoNodeProcessor(),
    'PixelNode': _getPixelNodeProcessor(),
    'HoursOfOperationNode':_getHOONodeProcessor()
  };

  return function (nodeTypeId) {
    return _.merge({}, defaultNodeProcessor, nodeTypeProcessors[nodeTypeId]);
  };

  // Default Node Processor Functions - START
  function attachNode(accountId, tree, parentNodeId, node, name, onSuccess, criteria) {
    const parentIsRoot = parentNodeId == 'root',
      parentNode = parentIsRoot ? tree.root : tree.nodes[parentNodeId],
      url = _serviceBaseUrl(accountId) + '/node',
      _criteria = criteria ? Array.isArray(criteria) ? criteria : [criteria] : [],
      stepName = name ? _.startCase(name) + ': ' + _.startCase(node.nodeType) : _.startCase(node.nodeType),
      data = {
        name: name,
        node: {
          nodeTypeId: node.nodeTypeId,
          stepName: stepName,
          settings: {}
        },
        ivrTree: tree,
        parentNodeId: parentNodeId,
        criteria: _replaceTagCriteriaNodeIdToken(tree, parentNodeId, _criteria),
        index: parentNode.childLinks.length,
        onSuccess: onSuccess,
        failOnError: false,
      };
    return $http.post(url, data).then(unpackResult).catch(unpackResult);
  }

  function updateNode(accountId, tree, node) {
    const url = _serviceBaseUrl(accountId) + '/editNode',
      data = {
        nodeId: node.id,
        node: node,
        ivrTree: tree,
        failOnError: false
      };
    return $http.patch(url, data).then(unpackResult).catch(unpackResult);
  }

  function updateLink(accountId, tree, nodeId, parentNodeId, index, criteria, onSuccess, metaData) {
    const url = _serviceBaseUrl(accountId) + '/editLink',
      childLinkIndex = _.findIndex(tree.nodes[parentNodeId].childLinks, {
        childNodeId: nodeId
      }),
      originalChildLink = tree.nodes[parentNodeId].childLinks[childLinkIndex],
      data = {
        parentNodeId: parentNodeId,
        nodeId: nodeId,
        index: typeof index === 'number' ? index : childLinkIndex,
        onSuccess: typeof onSuccess === 'boolean' ? onSuccess : originalChildLink.onSuccess,
        metaData: metaData ? metaData : originalChildLink.metaData,
        criteria: criteria ? criteria : originalChildLink.criteria,
        ivrTree: tree
      };
    return $http.patch(url, data).then(unpackResult).catch(unpackResult);
  }

  function removeNode(accountId, tree, nodeId, parentNodeId) {
    const url = _serviceBaseUrl(accountId) + '/removeNode/',
      data = {
        nodeId: nodeId,
        ivrTree: tree,
        parentNodeId: parentNodeId
      };
    return $http.patch(url, data).then(unpackResult).catch(unpackResult);
  }

  function getAvailableNodes(accountId, tree, parentNodeId) {
    const url = _serviceBaseUrl(accountId) + '/availableNodes';
    const data = {
      parentNodeId: parentNodeId,
      ivrTree: tree
    };
    return $http.post(url, data).then(function (result) {
      return result.data.nodes;
    });
  }

  function getAvailableTags(accountId, tree, parentNodeId) {
    const url = _serviceBaseUrl(accountId) + '/availableTags';
    const data = {
      parentNodeId: parentNodeId,
      ivrTree: tree
    };
    return $http.post(url, data).then(function (result) {
      return result.data.tags;
    });
  }

  function getNodeTypeIcon() {
    return 'glyphicon glyphicon-star';
  }

  function unpackResult(result) {
    result.data.tree = result.data.tree || result.data.ivrTree;

    if (result.data.nodeErrors) {
      const nodeErrorsById = {};
      // Capitalize error message. If needed elsewhere consider creating filter.
      _.forEach(result.data.nodeErrors, function (e) {
        if (e.errors) {
          for (let i = 0; i < e.errors.length; i++) {
            const errorMessage = e.errors[i];
            e.errors[i] = errorMessage.charAt(0).toUpperCase() + errorMessage.substr(1);
          }
        }
        nodeErrorsById[e.nodeId] = _.defaults({}, nodeErrorsById[e.nodeId], e);
        nodeErrorsById[e.nodeId].propertyErrors = _.keyBy(nodeErrorsById[e.nodeId].propertyErrors, 'propertyName');
      });

      result.data.nodeErrors = nodeErrorsById;
    }

    const gotoNodes = _.filter(result.data.tree.nodes, function(node) {
      return node.node.nodeTypeId === 'GoToNode';
    });
    _.forEach(gotoNodes, function (gotoNode) {
      const gotoDestinationNodeId = gotoNode.node.settings.goToNodeId;
      if (gotoDestinationNodeId) {
        // Set the respective goto source node ID and destination node ID reference on goto destination node and source node
        const destinationNode = result.data.tree.nodes[gotoDestinationNodeId].node;
        gotoNode.node.destinationNode = {
          iconCssClass: _getNodeTypeIcon(destinationNode.nodeTypeId),
          stepName: destinationNode.stepName,
          id: destinationNode.id
        };
        result.data.tree.nodes[gotoDestinationNodeId].node.gotoNodeSourceId = result.data.tree.nodes[gotoDestinationNodeId].node.gotoNodeSourceId || [];
        result.data.tree.nodes[gotoDestinationNodeId].node.gotoNodeSourceId.push({
          id: gotoNode.node.id,
          label: gotoNode.node.stepName
        });
      }
    });

    if (result.data.errors) {
      result.data.treeErrors = result.data.errors;
    }
    return result.data;
  }

  function _getNodeTypeIcon(nodeTypeId) {
    const nodeProcessor = nodeTypeProcessors[nodeTypeId];
    if (nodeProcessor) {
      return nodeProcessor.getNodeTypeIcon();
    } else {
      return getNodeTypeIcon();
    }    
  }

  function _serviceBaseUrl(accountId, treeId) {
    return ngAuthSettings.apiServiceBaseUri + accountId + '/ivrtree' + (treeId ? '/' + treeId : '');
  }

  function _replaceTagCriteriaNodeIdToken(tree, parentNodeId, criteria) {
    if (tree && !_.isEmpty(tree.nodes) && criteria.length) {
      // Replace menu id token in tagCriteria
      if(tree.nodes[parentNodeId].node.nodeTypeId == 'MenuNode') {
        _replaceNodeIdTokensWithActualNodeId(criteria, parentNodeId, '[menuTagName or nodeId]');
      }

      _replaceNodeIdTokensWithActualNodeId(criteria, parentNodeId, '[nodeId]');
    }

    return criteria;
  }
  
  function _replaceNodeIdTokensWithActualNodeId(criteria, parentNodeId, token) {
    _.forEach(criteria, function (c) {
      if (c.tagCriteria) {
        for (let i = 0; i < c.tagCriteria.length; i++) {
          if (c.tagCriteria[i].tagId) {
            c.tagCriteria[i].tagId = c.tagCriteria[i].tagId.replace(token, parentNodeId);
          }

          if (c.tagCriteria[i].tagIds) {
            c.tagCriteria[i].tagIds = _replaceNodeIdTokensInTagId(c.tagCriteria[i].tagIds, parentNodeId, token);
          }
        }
      }
    });
  }

  function _replaceNodeIdTokensInTagId(tagIds, parentNodeId, token) {
    const replacedTagIds = [];
    
    for (let i = 0; i < tagIds.length; i++) {
      replacedTagIds[i] = tagIds[i].replace(token, parentNodeId);
    }

    return replacedTagIds;
  }

  // Default Node Processor Functions - END

  // Node Type Specific Processors - START
  function _getRecordNodeProcessor() {
    return {
      getNodeTypeIcon: function () {
        return 'fa fa-microphone';
      }
    };
  }

  function _getMenuNodeProcessor() {
    return {
      getNodeTypeIcon: function () {
        return 'fa fa-list-ol';
      }
    };
  }

  function _getHangupNodeProcessor() {
    return {
      getNodeTypeIcon: function () {
        return 'fa fa-phone fa-rotate-180';
      }
    };
  }

  function _getDialNodeProcessor() {
    return {
      getNodeTypeIcon: function () {
        return 'fa fa-phone';
      }
    };
  }

  function _getGatherNodeProcessor() {
    return {
      getNodeTypeIcon: function () {
        return 'fa fa-keyboard-o';
      }
    };
  }

  function _getHOONodeProcessor() {
    return {
      getNodeTypeIcon: function () {
        return 'fa fa-clock-o';
      }
    };
  }

  function _getGotoNodeProcessor() {
    return {
      getNodeTypeIcon: function () {
        return 'fa fa-reply';
      }
    };
  }

  function _getPixelNodeProcessor() {
    return {
      getNodeTypeIcon: function () {
        return 'fa fa-send-o';
      }
    };
  }

  function _getRouterNodeProcessor() {
    return {
      attachNode: function (accountId, tree, parentNodeId, node, name, onSuccess, criteria) {
        const parentIsRoot = parentNodeId == 'root',
          parentNode = parentIsRoot ? tree.root : tree.nodes[parentNodeId],
          url = _serviceBaseUrl(accountId) + '/node',
          _criteria = criteria ? Array.isArray(criteria) ? criteria : [criteria] : [],
          stepName = name ? _.startCase(name) + ': ' + _.startCase(node.nodeType) : _.startCase(node.nodeType),
          data = {
            name: name,
            node: {
              nodeTypeId: node.nodeTypeId,
              stepName: stepName,
              settings: {}
            },
            ivrTree: tree,
            parentNodeId: parentNodeId,
            criteria: _criteria,
            index: parentNode.childLinks.length,
            onSuccess: onSuccess,
            failOnError: false,
          };
        return $http.post(url, data).then(this._markNewNodeAddedToRouter).catch(this._markNewNodeAddedToRouter);
      },
      updateLink: function (accountId, tree, nodeId, parentNodeId, index, criteria, onSuccess, metaData) {
        const parentNode = tree.nodes[parentNodeId],
          oldIndex = _.findIndex(parentNode.childLinks, {
            childNodeId: nodeId
          });

        let childNodeIdsToUpdate;
        if (oldIndex > -1 && oldIndex != index) {
          const startChangeIndex = oldIndex > index ? index : oldIndex,
            endChangeIndex = oldIndex > index ? oldIndex : index;
          childNodeIdsToUpdate = [];

          for (let i = startChangeIndex; i <= endChangeIndex; i++) {
            childNodeIdsToUpdate.push(parentNode.childLinks[i].childNodeId);
          }
        }
        const url = _serviceBaseUrl(accountId) + '/editLink',
          childLinkIndex = _.findIndex(tree.nodes[parentNodeId].childLinks, {
            childNodeId: nodeId
          }),
          originalChildLink = tree.nodes[parentNodeId].childLinks[childLinkIndex],
          data = {
            parentNodeId: parentNodeId,
            nodeId: nodeId,
            index: typeof index === 'number' ? index : childLinkIndex,
            onSuccess: typeof onSuccess === 'boolean' ? onSuccess : originalChildLink.onSuccess,
            metaData: metaData ? metaData : originalChildLink.metaData,
            criteria: criteria ? criteria : originalChildLink.criteria,
            ivrTree: tree
          };
        return $http.patch(url, data)
          .then(this._updateRouterChildNodesAndLinks.bind({
            childNodeIdsToUpdate: childNodeIdsToUpdate,
            parentNodeId: parentNodeId
          }))
          .catch(this._updateRouterChildNodesAndLinks.bind({
            childNodeIdsToUpdate: childNodeIdsToUpdate,
            parentNodeId: parentNodeId
          }));
      },
      removeNode: function (accountId, tree, nodeId) {
        const url = _serviceBaseUrl(accountId) + '/removeNode/',
          childNodeIdsToUpdate = [];
        let parentNodeId;
        const data = {
          nodeId: nodeId,
          ivrTree: tree
        };
        _.forEach(tree.nodes, function (node) {
          const childLinksLength = node.childLinks.length;
          if (childLinksLength) {
            const childNodeIndex = _.findIndex(node.childLinks, {
              childNodeId: nodeId
            });
            if (childNodeIndex > -1 && childNodeIndex + 1 < childLinksLength) {
              const startChangeIndex = childNodeIndex + 1;
              parentNodeId = node.node.id;

              for (let i = startChangeIndex; i < childLinksLength; i++) {
                childNodeIdsToUpdate.push(node.childLinks[i].childNodeId);
              }
            }
          }
        });
        return $http.patch(url, data)
          .then(this._updateRouterChildNodesAndLinks.bind({
            childNodeIdsToUpdate: childNodeIdsToUpdate,
            parentNodeId: parentNodeId
          }))
          .catch(this._updateRouterChildNodesAndLinks.bind({
            childNodeIdsToUpdate: childNodeIdsToUpdate,
            parentNodeId: parentNodeId
          }));
      },
      getNodeTypeIcon: function () {
        return 'fa fa-code-fork fa-rotate-90 m-l-3';
      },
      _updateRouterChildNodesAndLinks: function (result) {
        const childNodeIdsToUpdate = this.childNodeIdsToUpdate,
          parentNodeId = this.parentNodeId;
        if (Array.isArray(childNodeIdsToUpdate) && childNodeIdsToUpdate.length) {
          result.data.isRouterNodeLinkAdded = true;
          const updatedTree = result.data.tree || result.data.ivrTree;
          childNodeIdsToUpdate.reduceRight(function (updatedTree, childNodeId) {
            const updatedParent = updatedTree.nodes[parentNodeId],
              newChildLinkIndex = _.findIndex(updatedParent.childLinks, {
                childNodeId: childNodeId
              });
            if (newChildLinkIndex > -1) {
              updatedParent.childLinks[newChildLinkIndex].name = 'Route ' + (newChildLinkIndex + 1);
              const updatedNode = updatedTree.nodes[childNodeId].node;
              updatedNode.stepName = 'Route ' + (newChildLinkIndex + 1) + ':' + updatedNode.stepName.split(':')[1];
            }
            return updatedTree;
          }, updatedTree);
        }

        return unpackResult(result);
      },
      _markNewNodeAddedToRouter: function (result) {
        result.data.isRouterNodeLinkAdded = true;
        return unpackResult(result);
      }
    };
  }
  // Node Type Specific Processors - END
}
